import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import CircularProgress from '../../GenericUIBlocks/CircularProgress';
import Typography from '../../GenericUIBlocks/Typography';
import {ProofRevisionValue} from '../../../utils/types';

import NewCancel from '../../../assets/images/modal-icons/new-cancel';
import FileUploadIcon from '../../../assets/images/hire-designer/upload';

import './styles.scss';

const ACCEPTED_FILE_TYPES = ['png', 'jpg', 'jpeg', 'pdf'];
const ACCEPTED_FILE_LABEL = 'png, jpg, jpeg, pdf';
const MAX_FILES_COUNT = 5;
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const submitProgressStyles: React.CSSProperties = {
  width: '20px',
  height: '20px',
  border: '2px solid #fff',
};

export interface ProofDenyFormValues {
  reason: string;
  files: File[];
}

interface ProofApprovalModalProps {
  open: boolean;
  loading?: boolean;
  currentTheme?: string | null | undefined;
  revision?: ProofRevisionValue;
  onClose: () => void;
  onSubmit?: (values: ProofDenyFormValues) => Promise<boolean | void | any>;
}

const getFilesValidationError = (files: File[]) => {
  if (files.length > MAX_FILES_COUNT) {
    return `Maximum ${MAX_FILES_COUNT} files allowed`;
  }

  const hasInvalidFile = files.some((file) => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    return !extension || !ACCEPTED_FILE_TYPES.includes(extension);
  });

  if (hasInvalidFile) {
    return `Supported formats: ${ACCEPTED_FILE_LABEL}`;
  }

  const hasOversizedFile = files.some((file) => file.size > MAX_FILE_SIZE_BYTES);

  return hasOversizedFile
    ? `Each file must be ${MAX_FILE_SIZE_MB}MB or less`
    : '';
};

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== '';

const formatRevision = (revision?: ProofRevisionValue) => {
  if (!hasValue(revision)) {
    return '';
  }

  if (typeof revision !== 'object') {
    return `Revision ${revision}`;
  }

  const currentRevision =
    revision.currentRevision ??
    revision.current_revision ??
    revision.current ??
    revision.revision;
  const totalRevision =
    revision.totalRevision ??
    revision.totalRevisions ??
    revision.total_revision ??
    revision.total_revisions ??
    revision.total;

  if (hasValue(currentRevision) && hasValue(totalRevision)) {
    return `Revision ${currentRevision} of ${totalRevision}`;
  }

  if (hasValue(currentRevision)) {
    return `Revision ${currentRevision}`;
  }

  return '';
};

const ProofApprovalModal: React.FC<ProofApprovalModalProps> = ({
  open,
  loading = false,
  currentTheme,
  revision,
  onClose,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState({reason: '', files: ''});

  useEffect(() => {
    if (!open) {
      setReason('');
      setFiles([]);
      setErrors({reason: '', files: ''});
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [loading, onClose, open]);

  if (!open) {
    return null;
  }

  const revisionLabel = formatRevision(revision);

  const validateForm = () => {
    const nextErrors = {
      reason: reason.trim() ? '' : 'Reason is required',
      files: getFilesValidationError(files),
    };

    setErrors(nextErrors);
    return !nextErrors.reason && !nextErrors.files;
  };

  const appendFiles = (selectedFiles: FileList | File[]) => {
    const incomingFiles = Array.from(selectedFiles || []);

    if (!incomingFiles.length) {
      return;
    }

    const nextFiles = [...files, ...incomingFiles];
    const fileError = getFilesValidationError(nextFiles);

    if (fileError) {
      setErrors((prev) => ({...prev, files: fileError}));
      return;
    }

    setFiles(nextFiles);
    setErrors((prev) => ({...prev, files: ''}));
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    appendFiles(event.target.files || []);
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    appendFiles(event.dataTransfer.files || []);
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (!validateForm()) {
      return;
    }

    const didSubmit = await onSubmit?.({reason, files});

    if (didSubmit !== false) {
      setReason('');
      setFiles([]);
      setErrors({reason: '', files: ''});
    }
  };

  return (
    <div
      className={`proof-approval-modal ${currentTheme === 'v2' ? 'v2' : ''}`}
    >
      <div
        className="proof-approval-modal-content"
        role="dialog"
        aria-modal="true"
      >
        <div className="proof-approval-header">
          <div className="revision-heading">
            <div className="revision-title-row">
              <Typography className="revision-title">
                Revision Details
              </Typography>
              <button
                type="button"
                className="proof-approval-close"
                aria-label="Close proof approval"
                onClick={onClose}
                disabled={loading}
              >
                <NewCancel />
              </button>
            </div>
            {revisionLabel && (
              <Typography className="revision-count">
                ({revisionLabel})
              </Typography>
            )}
          </div>
        </div>

        <form className="proof-approval-body" onSubmit={handleSubmit}>
          <div className="proof-approval-scroll-content">
            <Typography className="revision-copy">
              Please provide clear details on the revisions that are required. You
              can also upload images if needed. All revisions must be provided
              below{' '}
              <strong>(we cannot accept phone or email change requests).</strong>
            </Typography>

            <Typography className="revision-copy">
              For complicated requests, it's most effective to record a{' '}
              <button
                type="button"
                className="loom-link"
                onClick={() =>
                  window.open(
                    'https://www.loom.com/',
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                loom.
              </button>
            </Typography>

            <div
              className={`proof-form-field ${errors.reason ? 'has-error' : ''}`}
            >
              <label htmlFor="proof-deny-reason">
                Reason<span>*</span>
              </label>
              <textarea
                id="proof-deny-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setErrors((prev) => ({...prev, reason: ''}));
                }}
              />
              {errors.reason && (
                <Typography className="proof-error">{errors.reason}</Typography>
              )}
            </div>

            <div
              className={`proof-form-field ${errors.files ? 'has-error' : ''}`}
            >
              <label>Upload Images</label>
              <div
                className="proof-upload-area"
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  multiple
                  onChange={handleFileChange}
                />
                <span className="proof-upload-icon" aria-hidden="true">
                  <FileUploadIcon />
                </span>
                <Typography className="proof-upload-text">
                  <span>Upload a file</span> or drag and drop here
                </Typography>
                <Typography className="proof-upload-hint">
                  Supported formats: {ACCEPTED_FILE_LABEL}. Maximum{' '}
                  {MAX_FILES_COUNT} files, {MAX_FILE_SIZE_MB}MB each
                </Typography>
              </div>
              {errors.files && (
                <Typography className="proof-error">{errors.files}</Typography>
              )}
              {files.length > 0 && (
                <div className="proof-selected-files">
                  {files.map((file, index) => (
                    <div
                      className="proof-selected-file"
                      key={`${file.name}-${file.size}-${index}`}
                    >
                      <Typography>{file.name}</Typography>
                      <button
                        type="button"
                        onClick={() =>
                          setFiles((prev) =>
                            prev.filter((_, fileIndex) => fileIndex !== index)
                          )
                        }
                        aria-label={`Remove ${file.name}`}
                      >
                        <NewCancel />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="proof-approval-footer">
            <button
              type="submit"
              className="proof-deny-submit"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress style={submitProgressStyles} />
              ) : (
                'Deny Proof'
              )}
            </button>
            <button
              type="button"
              className="proof-cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProofApprovalModal;
